import React from 'react';
import { FileText, Building, Upload, Archive, Search, Scale, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { services } from '../mockData';

const iconMap = {
  FileText,
  Building,
  Upload,
  Archive,
  Search,
  Scale
};

const Services = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="section-large">
      <div className="container">
        {/* Section Header */}
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
            Attorney Services & Legal Document Solutions
          </h2>
          <p className="body-large" style={{ 
            color: 'var(--text-secondary)',
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            Primarily serving legal professionals with high-volume litigation support, while extending our exceptional standards to all businesses and individuals requiring professional legal document services.
          </p>
        </div>

        {/* Services Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '2rem',
          marginBottom: '4rem'
        }}>
          {services.map((service) => {
            const IconComponent = iconMap[service.icon];
            const isPrimary = service.isPrimary;
            
            return (
              <div 
                key={service.id}
                className={`service-card ${isPrimary ? 'primary-service' : ''}`}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1.5rem',
                  ...(isPrimary && {
                    gridColumn: 'span 2',
                    background: 'linear-gradient(135deg, rgba(44, 90, 160, 0.05) 0%, rgba(30, 64, 175, 0.1) 100%)',
                    border: '2px solid var(--brand-primary)',
                    transform: 'scale(1.02)',
                    position: 'relative'
                  })
                }}
              >
                {/* Primary Service Badge */}
                {isPrimary && (
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    right: '20px',
                    backgroundColor: 'var(--brand-primary)',
                    color: 'white',
                    padding: '0.5rem 1rem',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: '600'
                  }}>
                    PRIMARY SERVICE
                  </div>
                )}
                {/* Icon */}
                <div style={{
                  width: isPrimary ? '80px' : '60px',
                  height: isPrimary ? '80px' : '60px',
                  backgroundColor: 'var(--brand-primary)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '1rem',
                  ...(isPrimary && {
                    background: 'linear-gradient(135deg, var(--brand-primary) 0%, #1e40af 100%)',
                    boxShadow: '0 8px 20px rgba(44, 90, 160, 0.3)'
                  })
                }}>
                  <IconComponent 
                    size={isPrimary ? 36 : 28} 
                    color="var(--text-inverse)" 
                    alt={`${service.title} icon - ${service.description}`}
                    role="img"
                    aria-label={`${service.title} service icon`}
                  />
                </div>

                {/* Content */}
                <div>
                  <h3 className="heading-3" style={{
                    marginBottom: '1rem',
                    fontSize: isPrimary ? '1.8rem' : '1.5rem',
                    fontWeight: isPrimary ? '700' : '600',
                    color: isPrimary ? 'var(--brand-primary)' : 'var(--text-primary)'
                  }}>
                    {service.title}
                  </h3>
                  
                  <p className="body-small" style={{
                    marginBottom: '1.5rem',
                    color: 'var(--text-secondary)'
                  }}>
                    {service.description}
                  </p>

                  {/* Features List */}
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem'
                  }}>
                    {service.features.map((feature, index) => (
                      <li 
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem'
                        }}
                      >
                        <Check size={16} color="var(--brand-primary)" />
                        <span className="body-small" style={{ color: 'var(--text-primary)' }}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                <div style={{ marginTop: 'auto' }}>
                  {service.hasPage ? (
                    <Link 
                      to={service.pageUrl || "/attorney-service"}
                      className={isPrimary ? "btn-primary" : "btn-secondary"}
                      style={{ 
                        width: '100%',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: isPrimary ? '1.1rem' : '1rem',
                        padding: isPrimary ? '1.2rem 2rem' : '1rem 1.5rem',
                        fontWeight: isPrimary ? '700' : '600',
                        ...(isPrimary && {
                          boxShadow: '0 6px 20px rgba(44, 90, 160, 0.3)'
                        })
                      }}
                    >
                      {isPrimary ? 'Access Attorney Services' : 'Learn More'}
                    </Link>
                  ) : (
                    <Link to="/quote" className="btn-secondary" style={{ width: '100%', textDecoration: 'none', display: 'inline-flex', justifyContent: 'center' }}>
                      Get Quote
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA Section */}
        <div style={{
          backgroundColor: 'var(--bg-card)',
          border: '1px solid var(--border-medium)',
          borderRadius: '12px',
          padding: '3rem',
          textAlign: 'center'
        }}>
          <h3 className="heading-2" style={{ 
            marginBottom: '1rem',
            fontSize: '2rem'
          }}>
            Legal Professionals & High-Volume Clients
          </h3>
          <p className="body-medium" style={{ 
            color: 'var(--text-secondary)',
            marginBottom: '2rem',
            maxWidth: '600px',
            margin: '0 auto 2rem auto'
          }}>
            Law firms and legal professionals get priority access to our specialized platform and volume discounts. Non-legal clients receive the same exceptional service standards. Contact us for custom solutions.
          </p>
          <Link to="/quote" className="btn-primary" data-testid="services-package-quote-link" style={{ fontSize: '1.1rem', padding: '1.2em 2.5em', textDecoration: 'none', display: 'inline-flex' }}>
            Request Package Quote
          </Link>
        </div>
      </div>

      <style>{`
        .primary-service {
          box-shadow: 0 12px 30px rgba(44, 90, 160, 0.15) !important;
        }
        
        @media (max-width: 1200px) {
          .primary-service {
            grid-column: span 1 !important;
            transform: scale(1) !important;
          }
        }
        
        @media (max-width: 767px) {
          .service-card {
            min-width: 280px !important;
          }
          
          div[style*="gridTemplateColumns"] {
            grid-template-columns: 1fr !important;
          }
          
          .primary-service {
            grid-column: span 1 !important;
            transform: scale(1) !important;
          }
        }
      `}</style>
    </section>
  );
};

export default Services;